import { ListSubheader, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { Box } from "@mui/system"
import { useMemo, useState } from "react"

import { useDateTime } from "../../../../localization/useDateTime"
import { getOrderPerson } from "../../../../../shared/utils/getOrderPerson"
import { escapeRegExp, sanitizeSearchQuery } from "../../../../../lib/stringLib"
import { SearchableText } from "../../../../../shared/components/search/Searchable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useMoney } from "../../../../currency/useMoney"
import { toDineroOrUndefined } from "../../../../currency/currencyLib"
import { AnyMeeting, hasDeliveryDetails } from "../../../../nonAdminUser/reports/MeetingsReceipt/UserMeetingsReceipt"
import { AnyMeetingServiceOrder } from "../../production/meetingService/components/OrderPackageSlip"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectUserType } from "../../../../login/loginSelectors"
import { UserType } from "../../../../login/loginTypes"

import { SearchOrdersInPopup } from "./SearchOrdersInPopup"
import {
    getCanteenNameFromOrder,
    getCustomerEmailFromOrder,
    getDeliveryTimeFromOrder,
    getMonetaryAmountFromOrder,
    getPaymentMethodFromOrder,
    groupByDay,
    hasCustomer,
    hasOrganizers,
} from "./receiptDropdownUtils"

const useStyles = makeStyles()((theme) => ({
    headerText: {
        fontSize: "1.125rem",
        fontWeight: 500,
        color: theme.palette.common.black,
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    orderSelect: {
        width: "30rem",
        maxWidth: "100%",
        "& .MuiSelect-select.MuiSelect-select": {
            padding: ".5rem 3.5rem .5rem .875rem",
        },
        "& .MuiSelect-icon": {
            right: "1rem",
        },
    },
    orderSelectHiddenOnMobile: {
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    layoutBlankFill: {
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    selectedOrderTitle: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    selectedOrderSummary: {
        minWidth: 0,
    },
    selectedOrderName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    selectedOrderNameMedium: {
        fontWeight: theme.typography.fontWeightMedium,
    },
    dropdownRow: {
        width: "100%",
        minWidth: 0,
    },
    dropdownContent: {
        minWidth: 0,
        flex: 1,
    },
    dropdownTitleRow: {
        minWidth: 0,
    },
    dropdownIdText: {
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
    },
    dropdownNameText: {
        fontWeight: theme.typography.fontWeightMedium,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    dropdownSecondaryText: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    dropdownAmountText: {
        fontWeight: theme.typography.fontWeightMedium,
        whiteSpace: "nowrap",
    },
    orderSelectItem: {
        width: "30rem",
        maxWidth: "calc(100vw - 2rem)",
        overflow: "hidden",
        ".MuiMenuItem-root:hover, .MuiMenuItem-root.Mui-focusVisible": {
            backgroundColor: tailwindPalette.slate[50],
        },
        ".MuiMenuItem-root.Mui-selected, .MuiMenuItem-root.Mui-selected:hover, .MuiMenuItem-root.Mui-selected.Mui-focusVisible":
            {
                backgroundColor: tailwindPalette.sky[50],
            },
        ".MuiMenu-list": {
            maxHeight: "24rem",
            overflowY: "auto",
            padding: 0,
        },
    },
    menuItem: {
        padding: ".625rem 1rem",
    },
    groupHeader: {
        display: "flex",
        justifyContent: "space-between",
    },
    listFooter: {
        top: "auto",
        bottom: 0,
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
}))

type OrdersType = AnyMeeting[] | AnyMeetingServiceOrder[]
type OrderType = AnyMeeting | AnyMeetingServiceOrder
type SlipVariant = "slip" | "receipt" | "pos"

const SelectDisplayReceiptComponent = ({ meeting }: Readonly<{ meeting?: AnyMeeting }>) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    return (
        <Typography className={classes.selectedOrderTitle}>
            {meeting?.meetingDetails.meetingName || ""}, #{meeting?.id}
            {meeting &&
                hasDeliveryDetails(meeting) &&
                meeting?.deliveryDetails?.time &&
                `, ${dateTimeFactory.formatDate(dateTimeFactory.fromApi(meeting?.deliveryDetails?.time))}`}
        </Typography>
    )
}

const SelectDisplayPosReceiptComponent = ({ order }: Readonly<{ order?: AnyMeeting }>) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const deliveryTime =
        order && "deliveries" in order && Array.isArray(order.deliveries)
            ? order.deliveries[0]?.deliveryTime
            : undefined
    const orderDate = deliveryTime
        ? dateTimeFactory.formatCustom(dateTimeFactory.fromApi(deliveryTime), "MMM d'th', yyyy")
        : ""

    return (
        <LocalizedStrict
            id="receipt-order-label"
            vars={{
                orderId: order?.id.toString() ?? "",
                orderDate,
            }}
        >
            <Typography className={classes.selectedOrderTitle}>
                Receipt #{order?.id.toString()}, {orderDate}
            </Typography>
        </LocalizedStrict>
    )
}

const SelectDisplaySlipComponent = ({ order }: Readonly<{ order?: AnyMeetingServiceOrder }>) => {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    return (
        <Typography className={classes.selectedOrderTitle}>
            {order?.meetingDetails.meetingName || ""}, #{order?.id},{" "}
            {dateTimeFactory.formatDate(dateTimeFactory.fromApi(order?.deliveryDetails.time ?? ""))}
        </Typography>
    )
}

const SelectedOrderTitle = ({ order, variant }: Readonly<{ order?: OrderType; variant: SlipVariant }>) => {
    if (variant === "pos") {
        return <SelectDisplayPosReceiptComponent order={order as AnyMeeting | undefined} />
    }
    if (variant === "receipt") {
        return <SelectDisplayReceiptComponent meeting={order as AnyMeeting | undefined} />
    }
    return <SelectDisplaySlipComponent order={order as AnyMeetingServiceOrder | undefined} />
}

function getCustomerName(order: OrderType) {
    if (hasOrganizers(order)) return getOrderPerson(order.organizers).person?.name
    if (hasCustomer(order)) return order.customer.displayName
    return undefined
}

function useDropdownOrderView(order: OrderType) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})
    const userType = useAppSelector(selectUserType)

    return useMemo(() => {
        const deliveryTime = getDeliveryTimeFromOrder(order)

        return {
            meetingName: order.meetingDetails?.meetingName || undefined,
            customerName: getCustomerName(order),
            secondaryText:
                userType === UserType.USER ? getCanteenNameFromOrder(order) : getCustomerEmailFromOrder(order),
            deliveryDate: deliveryTime ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(deliveryTime)) : undefined,
            totalAmount: formatOrderAmount(order, moneyFactory),
        }
    }, [order, userType, dateTimeFactory, moneyFactory])
}

// toDinero throws for currencies we do not support, the API text is the fallback then
function formatOrderAmount(order: OrderType, moneyFactory: ReturnType<typeof useMoney>) {
    const monetaryAmount = getMonetaryAmountFromOrder(order)
    if (!monetaryAmount) return undefined
    try {
        const dineroAmount = toDineroOrUndefined(monetaryAmount)
        return dineroAmount ? moneyFactory.format(dineroAmount) : monetaryAmount.formatted
    } catch {
        return monetaryAmount.formatted
    }
}

type SelectedOrderSummaryProps = Readonly<{
    order: OrderType
}>

const SelectedOrderSummary = ({ order }: SelectedOrderSummaryProps) => {
    const { classes, cx } = useStyles()
    const { meetingName, customerName, deliveryDate, totalAmount } = useDropdownOrderView(order)

    return (
        <Stack className={classes.selectedOrderSummary}>
            <Typography variant="body2" className={cx(classes.selectedOrderName, classes.selectedOrderNameMedium)}>
                {meetingName ?? customerName}
            </Typography>
            <Typography variant="caption" color="textSecondary" className={classes.selectedOrderName}>
                {[`#${order.id}`, deliveryDate, totalAmount].filter(Boolean).join(" · ")}
            </Typography>
        </Stack>
    )
}

type ReceiptDisplayDropdownMenuProps = Readonly<{
    order: OrderType
    searchRegex: RegExp | undefined
}>

export const ReceiptDisplayDropdownMenu = ({ order, searchRegex }: ReceiptDisplayDropdownMenuProps) => {
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const { meetingName, customerName, secondaryText, totalAmount } = useDropdownOrderView(order)

    const paymentMethodLabel = useMemo(() => {
        const paymentMethod = getPaymentMethodFromOrder(order)
        if (!paymentMethod) return undefined
        return l10n.getStringForEnum("receipt-payment-method", paymentMethod, paymentMethod)
    }, [order, l10n])

    const amountElement = totalAmount && <Typography className={classes.dropdownAmountText}>{totalAmount}</Typography>

    return (
        <Stack direction="row" alignItems="center" spacing={3} className={classes.dropdownRow}>
            <Stack className={classes.dropdownContent}>
                <Stack direction="row" alignItems="baseline" spacing={1} className={classes.dropdownTitleRow}>
                    <Typography variant="body2" className={classes.dropdownIdText}>
                        <SearchableText search={searchRegex}>{`#${order.id}`}</SearchableText>
                    </Typography>
                    <Typography className={classes.dropdownNameText}>
                        <SearchableText search={searchRegex}>
                            {[meetingName, customerName].filter(Boolean).join(" · ")}
                        </SearchableText>
                    </Typography>
                </Stack>
                {secondaryText && (
                    <Typography variant="body2" color="textSecondary" className={classes.dropdownSecondaryText}>
                        <SearchableText search={searchRegex}>{secondaryText}</SearchableText>
                    </Typography>
                )}
            </Stack>
            {paymentMethodLabel && amountElement ? (
                <Tooltip title={paymentMethodLabel} arrow>
                    {amountElement}
                </Tooltip>
            ) : (
                amountElement
            )}
        </Stack>
    )
}

// pos passes IMyOrderDetails here, meeting flows pass MeetingDetailsOrder
type OrderDetailsForTitle = Readonly<{
    id: number
    deliveries?: ReadonlyArray<Readonly<{ deliveryTime?: string }>>
    meetingDetails?: Readonly<{ meetingName: string }>
}>

type SlipModalHeaderProps = {
    isAuthenticated: boolean
    isSelectMode: boolean
    orders: OrdersType
    orderDetails?: OrderDetailsForTitle
    selectedIndex: number
    onOrderChange: (index: number) => void
    orderIdsLength?: number
    variant?: SlipVariant
}

export const ReceiptAndSlipDialogHeader = ({
    isAuthenticated,
    isSelectMode,
    orders,
    orderDetails,
    selectedIndex,
    onOrderChange,
    variant = "receipt",
    orderIdsLength,
}: SlipModalHeaderProps) => {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const selectedOrder: OrderType | undefined = orders[selectedIndex]
    const [searchQuery, setSearchQuery] = useState("")

    const filteredOrders = useMemo(() => {
        const searchLower = sanitizeSearchQuery(searchQuery ?? "").toLowerCase()

        return orders
            .map((order: OrderType, index) => ({ order, index }))
            .filter(({ order }) => {
                if (!searchLower) return true

                const organizers = hasOrganizers(order) ? order.organizers : []
                const orderId = order.id?.toString() || ""
                const meetingName = order.meetingDetails?.meetingName?.toLowerCase() || ""
                const organizerNames = hasOrganizers(order)
                    ? organizers?.map((org) => org.name?.toLowerCase() || "").join(" ")
                    : ""
                const customerName = hasCustomer(order) ? order.customer.displayName.toLowerCase() : ""
                const email = getCustomerEmailFromOrder(order)?.toLowerCase() ?? ""

                return (
                    orderId.includes(searchLower) ||
                    meetingName.includes(searchLower) ||
                    organizerNames.includes(searchLower) ||
                    customerName.includes(searchLower) ||
                    email.includes(searchLower)
                )
            })
    }, [searchQuery, orders])

    // the search text is escaped: unescaped input like "(" throws while building the regex
    const searchRegex = useMemo(
        () => (searchQuery ? new RegExp(`(${escapeRegExp(searchQuery)})`, "i") : undefined),
        [searchQuery],
    )

    const orderGroups = useMemo(
        () =>
            groupByDay(
                filteredOrders,
                ({ order }) => {
                    const deliveryTime = getDeliveryTimeFromOrder(order)
                    return deliveryTime ? dateTimeFactory.fromApi(deliveryTime) : undefined
                },
                dateTimeFactory.now(),
            ),
        [filteredOrders, dateTimeFactory],
    )

    if (isSelectMode) {
        return (
            <LocalizedStrict
                id={
                    variant === "slip"
                        ? "production-package-slip-header-orders-count"
                        : variant === "receipt"
                          ? "user-order-receipt-page-header-orders-count"
                          : "user-pos-order-receipt-page-header-orders-count"
                }
                vars={{ orderCount: orderIdsLength || 0 }}
            >
                <Typography className={classes.headerText}>Packing slips: {orderIdsLength} selected</Typography>
            </LocalizedStrict>
        )
    }

    if (isAuthenticated && orders.length > 1) {
        return (
            <Select<number | "">
                // pos keeps its title hidden on mobile, so the picker would be an empty box there
                className={cx(classes.orderSelect, variant === "pos" && classes.orderSelectHiddenOnMobile)}
                value={selectedOrder ? selectedIndex : ""}
                onChange={(event) => onOrderChange(Number(event.target.value))}
                renderValue={() => selectedOrder && <SelectedOrderSummary order={selectedOrder} />}
                displayEmpty={false}
                variant="outlined"
                size="medium"
                MenuProps={{
                    PaperProps: {
                        className: classes.orderSelectItem,
                    },
                }}
            >
                <SearchOrdersInPopup searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                {orderGroups.flatMap((group) => [
                    <ListSubheader
                        disableSticky
                        key={group.day?.toISODate() ?? "no-date"}
                        className={classes.groupHeader}
                    >
                        {group.day && group.day.hasSame(dateTimeFactory.now(), "day") ? (
                            <LocalizedStrict
                                id="merchant-receipt-dropdown-group-today-label"
                                vars={{ date: dateTimeFactory.formatDayAndDate(group.day) }}
                            >
                                <span>Today · {dateTimeFactory.formatDayAndDate(group.day)}</span>
                            </LocalizedStrict>
                        ) : (
                            <span>{group.day && dateTimeFactory.formatDayAndDate(group.day)}</span>
                        )}
                        <LocalizedStrict
                            id="merchant-receipt-dropdown-orders-count"
                            vars={{ count: group.items.length }}
                        >
                            <span>{group.items.length} orders</span>
                        </LocalizedStrict>
                    </ListSubheader>,
                    ...group.items.map(({ order, index }) => (
                        <MenuItem divider key={`${order.uid}-${index}`} value={index} className={classes.menuItem}>
                            <ReceiptDisplayDropdownMenu order={order} searchRegex={searchRegex} />
                        </MenuItem>
                    )),
                ])}
                <ListSubheader className={classes.listFooter}>
                    <LocalizedStrict
                        id="merchant-receipt-dropdown-orders-count"
                        vars={{ count: filteredOrders.length }}
                    >
                        <span>{filteredOrders.length} orders</span>
                    </LocalizedStrict>
                </ListSubheader>
            </Select>
        )
    }

    if (isAuthenticated && orders.length === 1) {
        return <SelectedOrderTitle order={selectedOrder ?? orders[0]} variant={variant} />
    }

    if (orderDetails) {
        if (variant === "pos") {
            const deliveryTime = orderDetails.deliveries?.[0]?.deliveryTime
            const orderDate = deliveryTime
                ? dateTimeFactory.formatCustom(dateTimeFactory.fromApi(deliveryTime), "MMM d'th', yyyy")
                : ""

            return (
                <LocalizedStrict id="receipt-order-label" vars={{ orderId: orderDetails.id.toString(), orderDate }}>
                    <Typography className={classes.headerText}>
                        Receipt #{orderDetails.id.toString()}, {orderDate}
                    </Typography>
                </LocalizedStrict>
            )
        }

        return (
            <Typography className={classes.headerText}>
                {[orderDetails.meetingDetails?.meetingName, `#${orderDetails.id}`].filter(Boolean).join(", ")}
            </Typography>
        )
    }

    return <Box className={classes.layoutBlankFill} />
}
